import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { Dashboard } from '@/pages/Dashboard';
import { Pipelines } from '@/pages/Pipelines';
import { PipelineDetail } from '@/pages/PipelineDetail';
import { Accounts } from '@/pages/Accounts';
import { Sheets } from '@/pages/Sheets';
import { Events } from '@/pages/Events';
import { LastNote } from '@/pages/LastNote';
import { AiSummary } from '@/pages/AiSummary';
import { AiSummaryDetail } from '@/pages/AiSummaryDetail';
import { AiSummaryRunDetail } from '@/pages/AiSummaryRunDetail';
import { Billing } from '@/pages/Billing';
import { MetaLeads } from '@/pages/MetaLeads';
import { Hosting } from '@/pages/Hosting';
import { GoogleCalendar } from '@/pages/GoogleCalendar';
import { Widgets } from '@/pages/Widgets';
import { Login } from '@/pages/Login';

interface MeResponse {
  user: { name: string };
}

function useMe(): { data?: MeResponse; isLoading: boolean; isError: boolean } {
  return useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('not_authenticated');
      return res.json();
    },
    retry: false,
    staleTime: 60_000,
  });
}

function AuthGuard(): JSX.Element {
  const { data, isLoading, isError } = useMe();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-xs text-muted-foreground">
        Проверка сессии…
      </div>
    );
  }
  if (isError || !data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Layout />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<AuthGuard />}>
        <Route index element={<Dashboard />} />
        <Route path="pipelines" element={<Pipelines />} />
        <Route path="pipelines/:id" element={<PipelineDetail />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="sheets" element={<Sheets />} />
        <Route path="events" element={<Events />} />
        <Route path="last-note" element={<LastNote />} />
        <Route path="ai-summary" element={<AiSummary />} />
        <Route path="ai-summary/:id" element={<AiSummaryDetail />} />
        <Route path="ai-summary/:id/runs/:runId" element={<AiSummaryRunDetail />} />
        <Route path="billing" element={<Billing />} />
        <Route path="meta-leads" element={<MetaLeads />} />
        <Route path="hosting" element={<Hosting />} />
        <Route path="gcal" element={<GoogleCalendar />} />
        <Route path="widgets" element={<Widgets />} />
      </Route>
    </Routes>
  );
}
